export type Point = { x: number; y: number };

export type WhiteboardTool =
  | 'select'
  | 'hand'
  | 'pen'
  | 'highlighter'
  | 'eraser'
  | 'text'
  | 'sticky'
  | 'rectangle'
  | 'ellipse'
  | 'diamond'
  | 'arrow'
  | 'frame';

export type WhiteboardElementKind =
  | 'path'
  | 'highlighter'
  | 'text'
  | 'sticky'
  | 'rectangle'
  | 'ellipse'
  | 'diamond'
  | 'arrow'
  | 'frame'
  | 'image';

export interface WhiteboardElement {
  id: string;
  kind: WhiteboardElementKind;
  x: number;
  y: number;
  width: number;
  height: number;
  text?: string;
  points?: Point[];
  stroke: string;
  fill: string;
  strokeWidth: number;
  fontSize: number;
  opacity: number;
  src?: string;
  startBinding?: string;
  endBinding?: string;
}

export interface BoardView {
  x: number;
  y: number;
  scale: number;
}

export interface WhiteboardDocument {
  id: string;
  title: string;
  elements: WhiteboardElement[];
  view: BoardView;
  updatedAt: number;
}

export interface WhiteboardBundle {
  currentId: string;
  boards: WhiteboardDocument[];
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const STROKE_COLORS = [
  '#172033',
  '#2563eb',
  '#0891b2',
  '#7c3aed',
  '#dc2626',
  '#ea580c',
  '#16a34a',
];

export const FILL_COLORS = [
  '#ffffff',
  '#dbeafe',
  '#cffafe',
  '#ede9fe',
  '#fee2e2',
  '#ffedd5',
  '#dcfce7',
  '#fef3c7',
  'transparent',
];

export const STICKY_COLORS = ['#fef3a6', '#fed7aa', '#fecdd3', '#ddd6fe', '#bae6fd', '#bbf7d0', '#ffffff'];

export const TEMPLATE_DEFINITIONS = [
  { id: 'architecture', name: '系统架构图', desc: '快速梳理端、服务与数据层', color: '#dbeafe', icon: '架' },
  { id: 'brainstorm', name: '头脑风暴', desc: '围绕一个主题发散和归类', color: '#fef3c7', icon: '想' },
  { id: 'flow', name: '流程讨论', desc: '从开始到决策与执行', color: '#dcfce7', icon: '流' },
  { id: 'retro', name: '会议复盘', desc: '做得好、待改进、下一步', color: '#ede9fe', icon: '复' },
] as const;

export type TemplateId = (typeof TEMPLATE_DEFINITIONS)[number]['id'];

export const uid = (prefix = 'wb') => {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${random}`;
};

export const cloneElements = (elements: WhiteboardElement[]): WhiteboardElement[] =>
  elements.map((element) => ({
    ...element,
    points: element.points?.map((point) => ({ ...point })),
  }));

export function createBoardDocument(title = '未命名画板'): WhiteboardDocument {
  return {
    id: uid('board'),
    title,
    elements: [],
    view: { x: 0, y: 0, scale: 1 },
    updatedAt: Date.now(),
  };
}

export function createElement(
  kind: WhiteboardElementKind,
  x: number,
  y: number,
  patch: Partial<WhiteboardElement> = {},
): WhiteboardElement {
  const defaults: Record<WhiteboardElementKind, Partial<WhiteboardElement>> = {
    path: { width: 0, height: 0, fill: 'transparent', strokeWidth: 3, fontSize: 18, opacity: 1, points: [{ x, y }] },
    highlighter: { width: 0, height: 0, fill: 'transparent', strokeWidth: 18, fontSize: 18, opacity: 0.32, points: [{ x, y }] },
    text: { width: 220, height: 62, fill: 'transparent', strokeWidth: 0, fontSize: 24, opacity: 1, text: '' },
    sticky: { width: 190, height: 160, fill: '#fef3a6', strokeWidth: 1, fontSize: 20, opacity: 1, text: '' },
    rectangle: { width: 190, height: 108, fill: '#ffffff', strokeWidth: 2, fontSize: 20, opacity: 1, text: '' },
    ellipse: { width: 180, height: 108, fill: '#ffffff', strokeWidth: 2, fontSize: 20, opacity: 1, text: '' },
    diamond: { width: 180, height: 122, fill: '#ffffff', strokeWidth: 2, fontSize: 19, opacity: 1, text: '' },
    arrow: { width: 180, height: 0, fill: 'transparent', strokeWidth: 2, fontSize: 16, opacity: 1, text: '' },
    frame: { width: 520, height: 340, fill: 'rgba(255,255,255,.22)', strokeWidth: 2, fontSize: 18, opacity: 1, text: '讨论区' },
    image: { width: 360, height: 240, fill: 'transparent', strokeWidth: 0, fontSize: 18, opacity: 1 },
  };

  return {
    id: uid(kind),
    kind,
    x,
    y,
    width: 0,
    height: 0,
    text: '',
    stroke: '#172033',
    fill: 'transparent',
    strokeWidth: 2,
    fontSize: 18,
    opacity: 1,
    ...defaults[kind],
    ...patch,
  };
}

export function normalizedBounds(x: number, y: number, width: number, height: number): Bounds {
  return {
    x: width < 0 ? x + width : x,
    y: height < 0 ? y + height : y,
    width: Math.abs(width),
    height: Math.abs(height),
  };
}

export function normalizeElement(element: WhiteboardElement): WhiteboardElement {
  if (element.kind === 'arrow' || element.kind === 'path' || element.kind === 'highlighter') return element;
  const bounds = normalizedBounds(element.x, element.y, element.width, element.height);
  return { ...element, ...bounds };
}

export function pathBounds(points: Point[] = []): Bounds {
  if (!points.length) return { x: 0, y: 0, width: 0, height: 0 };
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(1, Math.max(...xs) - minX),
    height: Math.max(1, Math.max(...ys) - minY),
  };
}

function centerOf(element: WhiteboardElement): Point {
  return { x: element.x + element.width / 2, y: element.y + element.height / 2 };
}

function anchorOnElement(element: WhiteboardElement, toward: Point): Point {
  const center = centerOf(element);
  const dx = toward.x - center.x;
  const dy = toward.y - center.y;
  if (Math.abs(dx) + Math.abs(dy) < 0.001) return center;

  const rx = Math.max(1, element.width / 2);
  const ry = Math.max(1, element.height / 2);
  let ratio = 1;
  if (element.kind === 'ellipse') {
    ratio = 1 / Math.sqrt((dx * dx) / (rx * rx) + (dy * dy) / (ry * ry));
  } else if (element.kind === 'diamond') {
    ratio = 1 / (Math.abs(dx) / rx + Math.abs(dy) / ry);
  } else {
    const ratioX = Math.abs(dx) < 0.001 ? Number.POSITIVE_INFINITY : rx / Math.abs(dx);
    const ratioY = Math.abs(dy) < 0.001 ? Number.POSITIVE_INFINITY : ry / Math.abs(dy);
    ratio = Math.min(ratioX, ratioY);
  }
  return { x: center.x + dx * ratio, y: center.y + dy * ratio };
}

export function isBindable(element?: WhiteboardElement): element is WhiteboardElement {
  return Boolean(element && ['sticky', 'rectangle', 'ellipse', 'diamond', 'image'].includes(element.kind));
}

export function getConnectorPoints(connector: WhiteboardElement, elements: WhiteboardElement[]): { start: Point; end: Point } {
  const rawStart = { x: connector.x, y: connector.y };
  const rawEnd = { x: connector.x + connector.width, y: connector.y + connector.height };
  const startElement = elements.find((element) => element.id === connector.startBinding);
  const endElement = elements.find((element) => element.id === connector.endBinding);
  const startTarget = isBindable(endElement) ? centerOf(endElement) : rawEnd;
  const endTarget = isBindable(startElement) ? centerOf(startElement) : rawStart;
  return {
    start: isBindable(startElement) ? anchorOnElement(startElement, startTarget) : rawStart,
    end: isBindable(endElement) ? anchorOnElement(endElement, endTarget) : rawEnd,
  };
}

export function getElementBounds(element: WhiteboardElement, elements: WhiteboardElement[] = []): Bounds {
  if (element.kind === 'path' || element.kind === 'highlighter') return pathBounds(element.points);
  if (element.kind === 'arrow') {
    const { start, end } = getConnectorPoints(element, elements);
    return normalizedBounds(start.x, start.y, end.x - start.x, end.y - start.y);
  }
  return normalizedBounds(element.x, element.y, element.width, element.height);
}

export function getElementsBounds(elements: WhiteboardElement[]): Bounds | null {
  if (!elements.length) return null;
  const bounds = elements.map((element) => getElementBounds(element, elements));
  const minX = Math.min(...bounds.map((bound) => bound.x));
  const minY = Math.min(...bounds.map((bound) => bound.y));
  const maxX = Math.max(...bounds.map((bound) => bound.x + bound.width));
  const maxY = Math.max(...bounds.map((bound) => bound.y + bound.height));
  return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
}

export function intersects(a: Bounds, b: Bounds): boolean {
  return a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y;
}

function distanceToSegment(point: Point, start: Point, end: Point): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (!lengthSquared) return Math.hypot(point.x - start.x, point.y - start.y);
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
}

export function hitTestElement(point: Point, element: WhiteboardElement, elements: WhiteboardElement[]): boolean {
  if (element.kind === 'path' || element.kind === 'highlighter') {
    const points = element.points || [];
    for (let index = 1; index < points.length; index += 1) {
      if (distanceToSegment(point, points[index - 1], points[index]) <= Math.max(7, element.strokeWidth / 2 + 3)) return true;
    }
    return points.length === 1 && Math.hypot(point.x - points[0].x, point.y - points[0].y) < 8;
  }
  if (element.kind === 'arrow') {
    const { start, end } = getConnectorPoints(element, elements);
    return distanceToSegment(point, start, end) <= Math.max(8, element.strokeWidth + 5);
  }
  const bounds = getElementBounds(element, elements);
  const px = (point.x - (bounds.x + bounds.width / 2)) / Math.max(1, bounds.width / 2);
  const py = (point.y - (bounds.y + bounds.height / 2)) / Math.max(1, bounds.height / 2);
  if (element.kind === 'ellipse') return px * px + py * py <= 1.06;
  if (element.kind === 'diamond') return Math.abs(px) + Math.abs(py) <= 1.06;
  if (element.kind === 'frame') {
    const inside = point.x >= bounds.x && point.x <= bounds.x + bounds.width && point.y >= bounds.y && point.y <= bounds.y + bounds.height;
    const inset = 10;
    const inner = point.x > bounds.x + inset && point.x < bounds.x + bounds.width - inset && point.y > bounds.y + inset && point.y < bounds.y + bounds.height - inset;
    return inside && !inner;
  }
  return point.x >= bounds.x && point.x <= bounds.x + bounds.width && point.y >= bounds.y && point.y <= bounds.y + bounds.height;
}

export function findTopElementAt(point: Point, elements: WhiteboardElement[], bindableOnly = false, excludeId?: string): WhiteboardElement | undefined {
  for (let index = elements.length - 1; index >= 0; index -= 1) {
    const element = elements[index];
    if (element.id === excludeId || (bindableOnly && !isBindable(element))) continue;
    if (hitTestElement(point, element, elements)) return element;
  }
  return undefined;
}

export function moveElement(element: WhiteboardElement, dx: number, dy: number): WhiteboardElement {
  if (element.kind === 'path' || element.kind === 'highlighter') {
    return { ...element, points: (element.points || []).map((point) => ({ x: point.x + dx, y: point.y + dy })) };
  }
  return { ...element, x: element.x + dx, y: element.y + dy };
}

export function smoothPath(points: Point[] = []): string {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y} l .01 .01`;
  if (points.length === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let index = 1; index < points.length - 1; index += 1) {
    const midpoint = {
      x: (points[index].x + points[index + 1].x) / 2,
      y: (points[index].y + points[index + 1].y) / 2,
    };
    path += ` Q ${points[index].x} ${points[index].y} ${midpoint.x} ${midpoint.y}`;
  }
  const last = points[points.length - 1];
  path += ` L ${last.x} ${last.y}`;
  return path;
}

function charWidth(char: string, fontSize: number): number {
  return /[\u2e80-\u9fff\uf900-\ufaff]/.test(char) ? fontSize : fontSize * 0.56;
}

export function wrapText(text: string, maxWidth: number, fontSize: number, maxLines = 12): string[] {
  if (!text) return [];
  const result: string[] = [];
  for (const sourceLine of text.split('\n')) {
    if (!sourceLine) {
      result.push('');
      continue;
    }
    let current = '';
    let width = 0;
    for (const char of sourceLine) {
      const nextWidth = charWidth(char, fontSize);
      if (current && width + nextWidth > Math.max(fontSize, maxWidth)) {
        result.push(current);
        current = char;
        width = nextWidth;
      } else {
        current += char;
        width += nextWidth;
      }
    }
    if (current) result.push(current);
  }
  if (result.length <= maxLines) return result;
  const clipped = result.slice(0, maxLines);
  clipped[maxLines - 1] = `${clipped[maxLines - 1].slice(0, -1)}…`;
  return clipped;
}

function connector(start: WhiteboardElement, end: WhiteboardElement, patch: Partial<WhiteboardElement> = {}): WhiteboardElement {
  const startCenter = centerOf(start);
  const endCenter = centerOf(end);
  return createElement('arrow', startCenter.x, startCenter.y, {
    width: endCenter.x - startCenter.x,
    height: endCenter.y - startCenter.y,
    startBinding: start.id,
    endBinding: end.id,
    stroke: '#64748b',
    ...patch,
  });
}

export function buildTemplate(templateId: TemplateId, center: Point): WhiteboardElement[] {
  if (templateId === 'architecture') {
    const title = createElement('text', center.x - 430, center.y - 265, { width: 860, height: 48, text: '系统架构讨论', fontSize: 30, stroke: '#0f172a' });
    const user = createElement('ellipse', center.x - 430, center.y - 62, { width: 150, height: 104, text: '用户端', fill: '#dbeafe', stroke: '#2563eb' });
    const gateway = createElement('rectangle', center.x - 190, center.y - 62, { width: 170, height: 104, text: '接入网关', fill: '#cffafe', stroke: '#0891b2' });
    const serviceA = createElement('rectangle', center.x + 80, center.y - 190, { width: 190, height: 86, text: '核心业务服务', fill: '#ede9fe', stroke: '#7c3aed' });
    const serviceB = createElement('rectangle', center.x + 80, center.y - 43, { width: 190, height: 86, text: '智能体服务', fill: '#ede9fe', stroke: '#7c3aed' });
    const serviceC = createElement('rectangle', center.x + 80, center.y + 104, { width: 190, height: 86, text: '消息与任务', fill: '#ede9fe', stroke: '#7c3aed' });
    const data = createElement('rectangle', center.x + 360, center.y - 62, { width: 170, height: 104, text: '数据与知识库', fill: '#dcfce7', stroke: '#16a34a' });
    return [
      title,
      connector(user, gateway),
      connector(gateway, serviceA),
      connector(gateway, serviceB),
      connector(gateway, serviceC),
      connector(serviceA, data),
      connector(serviceB, data),
      connector(serviceC, data),
      user,
      gateway,
      serviceA,
      serviceB,
      serviceC,
      data,
    ];
  }

  if (templateId === 'brainstorm') {
    const centerNode = createElement('ellipse', center.x - 105, center.y - 62, { width: 210, height: 124, text: '讨论主题', fill: '#fef3c7', stroke: '#d97706', fontSize: 24 });
    const notes = [
      createElement('sticky', center.x - 440, center.y - 230, { text: '用户需求', fill: '#bae6fd' }),
      createElement('sticky', center.x + 250, center.y - 230, { text: '核心价值', fill: '#ddd6fe' }),
      createElement('sticky', center.x - 440, center.y + 90, { text: '实现思路', fill: '#bbf7d0' }),
      createElement('sticky', center.x + 250, center.y + 90, { text: '风险与问题', fill: '#fecdd3' }),
    ];
    return [...notes.map((note) => connector(centerNode, note, { stroke: '#94a3b8' })), centerNode, ...notes];
  }

  if (templateId === 'flow') {
    const start = createElement('ellipse', center.x - 430, center.y - 54, { width: 150, height: 108, text: '开始', fill: '#dcfce7', stroke: '#16a34a' });
    const collect = createElement('rectangle', center.x - 190, center.y - 54, { width: 180, height: 108, text: '收集信息', fill: '#dbeafe', stroke: '#2563eb' });
    const decide = createElement('diamond', center.x + 80, center.y - 68, { width: 190, height: 136, text: '是否通过？', fill: '#fef3c7', stroke: '#d97706' });
    const execute = createElement('rectangle', center.x + 360, center.y - 54, { width: 180, height: 108, text: '执行方案', fill: '#ede9fe', stroke: '#7c3aed' });
    const revise = createElement('rectangle', center.x + 85, center.y + 180, { width: 180, height: 96, text: '补充与调整', fill: '#fee2e2', stroke: '#dc2626' });
    return [
      connector(start, collect),
      connector(collect, decide),
      connector(decide, execute),
      connector(decide, revise, { stroke: '#dc2626' }),
      connector(revise, collect, { stroke: '#dc2626' }),
      start,
      collect,
      decide,
      execute,
      revise,
    ];
  }

  const frameWidth = 300;
  const frameHeight = 420;
  const gap = 28;
  const startX = center.x - (frameWidth * 3 + gap * 2) / 2;
  const frameA = createElement('frame', startX, center.y - 230, { width: frameWidth, height: frameHeight, text: '做得好', stroke: '#16a34a', fill: 'rgba(220,252,231,.28)' });
  const frameB = createElement('frame', startX + frameWidth + gap, center.y - 230, { width: frameWidth, height: frameHeight, text: '待改进', stroke: '#ea580c', fill: 'rgba(255,237,213,.28)' });
  const frameC = createElement('frame', startX + (frameWidth + gap) * 2, center.y - 230, { width: frameWidth, height: frameHeight, text: '下一步', stroke: '#7c3aed', fill: 'rgba(237,233,254,.28)' });
  const notes = [
    createElement('sticky', frameA.x + 48, frameA.y + 88, { width: 200, height: 130, text: '写下值得保留的做法', fill: '#bbf7d0' }),
    createElement('sticky', frameB.x + 48, frameB.y + 88, { width: 200, height: 130, text: '记录问题和改进建议', fill: '#fed7aa' }),
    createElement('sticky', frameC.x + 48, frameC.y + 88, { width: 200, height: 130, text: '明确负责人和行动项', fill: '#ddd6fe' }),
  ];
  return [frameA, frameB, frameC, ...notes];
}
